import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtOdds, apiError } from "../lib/api";
import { BookLinks } from "../components/BookLinks";
import { Trash2, Plus, Save } from "lucide-react";

function americanToDecimal(a) {
  return 1 + (a > 0 ? a / 100 : 100 / Math.abs(a));
}

export default function Parlay() {
  const qc = useQueryClient();
  const [legs, setLegs] = useState([]);
  const [stake, setStake] = useState(50);
  const [name, setName] = useState("My Parlay");
  const [custom, setCustom] = useState({ label: "", odds: "", prob: "" });

  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: oddsData } = useQuery({ queryKey: ["odds"], queryFn: () => api.get("/odds").then((r) => r.data) });
  const { data: saved } = useQuery({ queryKey: ["parlays"], queryFn: () => api.get("/parlays").then((r) => r.data) });

  const rows = oddsData?.rows || [];
  const labelQ = custom.label.trim().toLowerCase();
  const alreadyPicked = labelQ.endsWith(" hr") && rows.some((r) => `${r.name} hr`.toLowerCase() === labelQ);
  const suggestions = labelQ.length >= 2 && !alreadyPicked
    ? rows.filter((r) => r.name.toLowerCase().includes(labelQ) || (r.team_name || r.team || "").toLowerCase().includes(labelQ)).slice(0, 6)
    : [];

  const saveMut = useMutation({
    mutationFn: () => api.post("/parlays", { name, stake, legs }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["parlays"] }); toast.success("Parlay saved"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/parlays/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["parlays"] }),
  });

  const addLeg = (leg) => {
    if (legs.some((l) => l.label === leg.label)) return toast.info("Leg already added");
    setLegs([...legs, leg]);
  };

  const addCustom = () => {
    const o = parseInt(custom.odds, 10);
    const p = parseFloat(custom.prob);
    if (!custom.label || isNaN(o) || isNaN(p) || p <= 0 || p >= 100) return toast.error("Fill label, odds and probability (1-99)");
    addLeg({ label: custom.label, odds: o, prob: p });
    setCustom({ label: "", odds: "", prob: "" });
  };

  const dec = legs.reduce((acc, l) => acc * americanToDecimal(l.odds), 1);
  const prob = legs.reduce((acc, l) => acc * (l.prob / 100), 1);
  const payout = stake * dec;
  const ev = stake * (dec - 1) * prob - stake * (1 - prob);
  const combinedAm = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Parlay <span className="gold-text">Builder</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Stack legs from today's board or add your own. Math done for you.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5 fade-up-1">
        <div className="space-y-4">
          <div className="card-surface rounded-lg p-4">
            <h3 className="font-display text-lg font-bold uppercase text-[#a855f7] mb-3">Quick Add — HR Props (best line)</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {rows.slice(0, 10).map((r) => (
                <button key={r.mlb_id} data-testid={`parlay-add-${r.mlb_id}`}
                  onClick={() => addLeg({ label: `${r.name} HR`, odds: r.best_odds, prob: r.model_prob })}
                  className="flex items-center justify-between text-left border border-white/10 hover:border-[#a855f7]/50 rounded px-3 py-2 text-sm transition-colors duration-200">
                  <span className="truncate">{r.name} <span className="text-neutral-600 text-xs">HR</span></span>
                  <span className="font-data text-[#22c55e] ml-2">{fmtOdds(r.best_odds)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="card-surface rounded-lg p-4">
            <h3 className="font-display text-lg font-bold uppercase text-[#a855f7] mb-1">Custom Leg</h3>
            <p className="text-xs text-neutral-500 mb-3">Type a player or team — best book odds and model win % fill in automatically. Or enter your own.</p>
            <div className="relative grid sm:grid-cols-[1fr_110px_110px_auto] gap-2">
              <div className="relative">
                <input placeholder="Player or team (e.g., Schwarber)" value={custom.label} onChange={(e) => setCustom({ ...custom, label: e.target.value })}
                  data-testid="parlay-custom-label" className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200" />
                {suggestions.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full card-surface rounded-lg max-h-56 overflow-y-auto shadow-xl" data-testid="parlay-suggestions">
                    {suggestions.map((r) => (
                      <button key={r.mlb_id} data-testid={`parlay-suggest-${r.mlb_id}`}
                        onClick={() => setCustom({ label: `${r.name} HR`, odds: String(r.best_odds), prob: String(r.model_prob) })}
                        className="flex items-center justify-between w-full text-left px-3 py-2 text-sm text-neutral-300 hover:bg-[#a855f7]/10 transition-colors duration-200">
                        <span className="flex items-center gap-2 truncate">
                          <img src={r.headshot} alt="" className="w-6 h-6 rounded-full object-cover bg-white/5" loading="lazy" />
                          <span className="truncate">{r.name} <span className="text-neutral-600 text-xs">{r.team} HR</span></span>
                        </span>
                        <span className="font-data text-xs shrink-0 ml-2"><span className="text-[#22c55e]">{fmtOdds(r.best_odds)}</span> <span className="text-neutral-500">· {r.model_prob}%{r.best_book ? ` · ${r.best_book}` : ""}</span></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input placeholder="Odds +150" value={custom.odds} onChange={(e) => setCustom({ ...custom, odds: e.target.value })}
                data-testid="parlay-custom-odds" className="bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm font-data outline-none transition-colors duration-200" />
              <input placeholder="Win %" value={custom.prob} onChange={(e) => setCustom({ ...custom, prob: e.target.value })}
                data-testid="parlay-custom-prob" className="bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm font-data outline-none transition-colors duration-200" />
              <button onClick={addCustom} data-testid="parlay-custom-add-btn" className="bg-white/10 hover:bg-[#a855f7] hover:text-black rounded px-3 py-2 transition-colors duration-200"><Plus size={16} /></button>
            </div>
          </div>

          {saved?.parlays?.length > 0 && (
            <div className="card-surface rounded-lg p-4">
              <h3 className="font-display text-lg font-bold uppercase text-neutral-300 mb-3">Saved Parlays</h3>
              <div className="space-y-2">
                {saved.parlays.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border border-white/10 rounded px-3 py-2 text-sm" data-testid={`saved-parlay-${p.id}`}>
                    <div>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-neutral-500 text-xs ml-2">{p.legs.length} legs · ${p.stake} → ${p.payout} · {p.win_prob}%</span>
                    </div>
                    <button onClick={() => delMut.mutate(p.id)} data-testid={`delete-parlay-${p.id}`} className="text-neutral-600 hover:text-red-400 transition-colors duration-200"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card-gold border border-[#a855f7]/30 rounded-xl p-5 h-fit lg:sticky lg:top-6 gold-glow">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="parlay-name-input"
            className="w-full bg-transparent font-display text-xl font-bold uppercase text-white outline-none border-b border-white/10 focus:border-[#a855f7] pb-1 transition-colors duration-200" />
          <div className="mt-3 space-y-2 min-h-[60px]">
            {legs.length === 0 && <p className="text-neutral-600 text-sm">No legs yet. Add from the left.</p>}
            {legs.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm border border-white/10 rounded px-2.5 py-1.5" data-testid={`parlay-leg-${i}`}>
                <span className="truncate">{l.label}</span>
                <span className="flex items-center gap-2 font-data">{fmtOdds(l.odds)}
                  <button onClick={() => setLegs(legs.filter((_, j) => j !== i))} data-testid={`parlay-remove-leg-${i}`} className="text-neutral-600 hover:text-red-400 transition-colors duration-200"><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">Stake ($)</label>
            <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value))} data-testid="parlay-stake-input"
              className="mt-1 w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 font-data outline-none transition-colors duration-200" />
          </div>
          {legs.length > 0 && (
            <div className="mt-4 space-y-1.5 text-sm border-t border-white/10 pt-3">
              <div className="flex justify-between"><span className="text-neutral-400">Combined odds</span><span className="font-data font-bold text-[#a855f7]" data-testid="parlay-combined-odds">{fmtOdds(combinedAm)} ({dec.toFixed(2)})</span></div>
              <div className="flex justify-between"><span className="text-neutral-400">Win probability</span><span className="font-data" data-testid="parlay-win-prob">{(prob * 100).toFixed(2)}%</span></div>
              <div className="flex justify-between"><span className="text-neutral-400">Payout</span><span className="font-data text-[#22c55e]" data-testid="parlay-payout">${payout.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold"><span className="text-neutral-300">Expected value</span><span className={`font-data ${ev >= 0 ? "text-[#22c55e]" : "text-red-400"}`} data-testid="parlay-ev">{ev >= 0 ? "+" : "-"}${Math.abs(ev).toFixed(2)}</span></div>
            </div>
          )}
          <button onClick={() => saveMut.mutate()} disabled={legs.length === 0 || saveMut.isPending} data-testid="parlay-save-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-40 text-black font-bold uppercase tracking-wider text-sm py-2.5 rounded transition-colors duration-200">
            <Save size={15} /> Save Parlay
          </button>
          {legs.length > 0 && (
            <div className="mt-4 border-t border-white/10 pt-3">
              <BookLinks title="Place this parlay at your book" testid="parlay-book-links" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
